import { useRacesList } from '../../races/contexts/races';
import AnimalSearch from '../../animals/forms/search';

// Campos de semen (api-reference/admin-semen.md, decisión 0021). El padre es un
// ANIMAL: se elige uno existente (sire_animal_id) o se crea un toro de referencia
// anidado (sire_animal). Al fijar el toro se autocompletan, EDITABLES:
//   código ← número (arete) | si no hay, registro    ·    nombre comercial ← nombre
// `allowNewSire` = false en edición (el PATCH solo linkea por id; el alta anidada
// es solo del POST).

export default function SemenFields({ form, setField, fieldError, disabled, showStatus = false, allowNewSire = true }) {
    const { data: racesData } = useRacesList({});
    const races = racesData?.items ?? [];

    const selectSire = (a) => {
        setField('sireAnimal', a);
        if (!form.codeTouched) setField('code', a.number || a.register || '');
        if (!form.nameTouched) setField('name', a.name || '');
        if (!form.raceTouched) {
            const rid = a.race_id != null ? a.race_id : a.race?.id;
            if (rid != null) setField('race_id', String(rid));
        }
    };
    const clearSire = () => setField('sireAnimal', null);

    const onNewName = (v) => { setField('newName', v); if (!form.nameTouched) setField('name', v); };
    const onNewRegister = (v) => { setField('newRegister', v); if (!form.codeTouched) setField('code', v); };
    const onNewRace = (v) => { setField('newRace', v); if (!form.raceTouched) setField('race_id', v); };

    const onCode = (v) => { setField('code', v); setField('codeTouched', true); };
    const onName = (v) => { setField('name', v); setField('nameTouched', true); };
    const onRace = (v) => { setField('race_id', v); setField('raceTouched', true); };

    const isNew = allowNewSire && form.sireMode === 'new';

    return (
        <div className="cat-form__grid">
            {/* ---- padre (toro) ---- */}
            <div className="cat-form__field cat-form__field--full">
                <span className="cat-form__label">Toro (padre)</span>
                {allowNewSire && (
                    <div className="cat-form__toggle" role="group" aria-label="Tipo de toro">
                        <button type="button" className={`cat-form__toggle-btn ${!isNew ? 'cat-form__toggle-btn--active' : ''}`} onClick={() => setField('sireMode', 'existing')} disabled={disabled}>Toro existente</button>
                        <button type="button" className={`cat-form__toggle-btn ${isNew ? 'cat-form__toggle-btn--active' : ''}`} onClick={() => setField('sireMode', 'new')} disabled={disabled}>Nuevo toro</button>
                    </div>
                )}
                {!isNew && (
                    <>
                        <AnimalSearch value={form.sireAnimal} sex="male" onSelect={selectSire} onClear={clearSire} placeholder="Buscar toro por nombre o registro…" />
                        {fieldError('sire') && <span className="cat-form__error" role="alert">{fieldError('sire')}</span>}
                    </>
                )}
            </div>

            {isNew && (
                <>
                    <label className="cat-form__field">
                        <span className="cat-form__label">Nombre del toro</span>
                        <input type="text" value={form.newName} onChange={(e) => onNewName(e.target.value)} maxLength={120} disabled={disabled} autoFocus aria-invalid={!!fieldError('newName')} />
                        {fieldError('newName') && <span className="cat-form__error" role="alert">{fieldError('newName')}</span>}
                    </label>
                    <label className="cat-form__field">
                        <span className="cat-form__label">Raza del toro</span>
                        <select value={form.newRace} onChange={(e) => onNewRace(e.target.value)} disabled={disabled} aria-invalid={!!fieldError('newRace')}>
                            <option value="">Seleccionar…</option>
                            {races.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                        {fieldError('newRace') && <span className="cat-form__error" role="alert">{fieldError('newRace')}</span>}
                    </label>
                    <label className="cat-form__field cat-form__field--full">
                        <span className="cat-form__label">Registro del toro</span>
                        <input type="text" value={form.newRegister} onChange={(e) => onNewRegister(e.target.value)} maxLength={60} disabled={disabled} placeholder="Opcional" aria-invalid={!!fieldError('newRegister')} />
                        <span className="cat-form__hint">Se registra como toro de referencia (externo).</span>
                        {fieldError('newRegister') && <span className="cat-form__error" role="alert">{fieldError('newRegister')}</span>}
                    </label>
                </>
            )}

            {/* ---- lote de semen ---- */}
            <label className="cat-form__field">
                <span className="cat-form__label">Código</span>
                <input type="text" value={form.code} onChange={(e) => onCode(e.target.value)} maxLength={40} disabled={disabled} placeholder="Lote / código único" aria-invalid={!!fieldError('code')} />
                {fieldError('code') && <span className="cat-form__error" role="alert">{fieldError('code')}</span>}
            </label>

            <label className="cat-form__field">
                <span className="cat-form__label">Nombre comercial</span>
                <input type="text" value={form.name} onChange={(e) => onName(e.target.value)} maxLength={120} disabled={disabled} placeholder="Opcional" aria-invalid={!!fieldError('name')} />
                {fieldError('name') && <span className="cat-form__error" role="alert">{fieldError('name')}</span>}
            </label>

            <label className="cat-form__field">
                <span className="cat-form__label">Raza del lote</span>
                <select value={form.race_id} onChange={(e) => onRace(e.target.value)} disabled={disabled}>
                    <option value="">Sin especificar</option>
                    {races.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
            </label>

            <label className="cat-form__field">
                <span className="cat-form__label">Origen</span>
                <select value={form.origin} onChange={(e) => setField('origin', e.target.value)} disabled={disabled}>
                    <option value="">Sin especificar</option>
                    <option value="national">Nacional</option>
                    <option value="imported">Importado</option>
                </select>
            </label>

            <label className="cat-form__field cat-form__field--full">
                <span className="cat-form__label">Proveedor</span>
                <input type="text" value={form.supplier} onChange={(e) => setField('supplier', e.target.value)} maxLength={120} disabled={disabled} placeholder="Casa de genética / proveedor (opcional)" aria-invalid={!!fieldError('supplier')} />
                {fieldError('supplier') && <span className="cat-form__error" role="alert">{fieldError('supplier')}</span>}
            </label>

            {showStatus && (
                <label className="cat-form__field">
                    <span className="cat-form__label">Estado</span>
                    <select value={form.status} onChange={(e) => setField('status', Number(e.target.value))} disabled={disabled}>
                        <option value={2}>Activo</option>
                        <option value={1}>Inactivo</option>
                    </select>
                </label>
            )}
        </div>
    );
}
